import { TranslationFunction } from '@/src/types/translations'
import { Box, Divider, Typography } from '@mui/material'

interface RefundBreakupProps {
    cancellationAirlineFee: string
    cancellationCleartripFee: string
    dateChangeAirlineFee: string
    dateChangeCleartripFee: string
    note: string
    variant: 'cleartrip' | 'flyin'
    formatCurrency: (amount: number) => string
    t: TranslationFunction<'itineraryPage.miniRules'>
}

const RefundBreakup = ({
    cancellationAirlineFee,
    cancellationCleartripFee,
    dateChangeAirlineFee,
    dateChangeCleartripFee,
    note,
    variant,
    formatCurrency,
    t
}: RefundBreakupProps) => {
    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                {t('standardFare')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Cancellation Fees */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            borderBottom: '1px dashed rgba(255,255,255,0.3)',
                            pb: 0.5,
                            mb: 1.5,
                            display: 'block'
                        }}
                    >
                        {t('cancellationFeePerPerson')}
                    </Typography>

                    {cancellationAirlineFee && cancellationAirlineFee !== '0.0' ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">{t('airlineFee')}</Typography>
                            <Typography variant="caption">{formatCurrency(Number(cancellationAirlineFee))}</Typography>
                        </Box>
                    ) : (
                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                            {t('nonRefundableMessage')}
                        </Typography>
                    )}

                    {cancellationAirlineFee &&
                        cancellationAirlineFee !== '0.0' &&
                        cancellationCleartripFee !== 'null' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption">{t(`${variant}Fee`)}</Typography>
                                <Typography variant="caption">
                                    {formatCurrency(Number(cancellationCleartripFee))}
                                </Typography>
                            </Box>
                        )}
                </Box>

                {/* Change Fees */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            borderBottom: '1px dashed rgba(255,255,255,0.3)',
                            pb: 0.5,
                            mb: 1.5,
                            display: 'block'
                        }}
                    >
                        {t('flightChangeFeePerPerson')}
                    </Typography>

                    {dateChangeAirlineFee && dateChangeAirlineFee !== '0.0' ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">{t('airlineFee')}</Typography>
                            <Typography variant="caption">{formatCurrency(Number(dateChangeAirlineFee))}</Typography>
                        </Box>
                    ) : (
                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                            {t('nonAmendableMessage')}
                        </Typography>
                    )}

                    {dateChangeAirlineFee && dateChangeAirlineFee !== '0.0' && dateChangeCleartripFee !== 'null' && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption">{t(`${variant}Fee`)}</Typography>
                            <Typography variant="caption">{formatCurrency(Number(dateChangeCleartripFee))}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {note && (
                <>
                    <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.3)' }} />
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {t('note')}
                    </Typography>
                </>
            )}
        </Box>
    )
}

export default RefundBreakup
